<template>
  <div>
    <Head title="Crear Devolución" />
    
    <!-- Encabezado -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center">
        <Link href="/devolutions" class="mr-4 p-2 text-white bg-gray-700 hover:bg-gray-800 rounded transition-colors" title="Volver">
          <i class="fa fa-arrow-left"></i>
        </Link>
        <div>
          <h1 class="text-3xl font-bold text-gray-800">Nueva Devolución</h1>
          <p class="text-sm text-gray-500">Gestión de reintegro de dinero, cambio de productos o abono a ventas mayores</p>
        </div>
      </div>
    </div>

    <form @submit.prevent="submit">
      <!-- PASO 1: SELECCIÓN DE FACTURA / VENTA A AFECTAR -->
      <div class="bg-white rounded-lg shadow mb-6 border border-gray-100 overflow-hidden">
        <div class="px-6 py-4 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
          <div class="flex items-center space-x-2">
            <span class="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">1</span>
            <h2 class="text-lg font-semibold text-gray-800">Seleccionar Factura / Venta a Afectar</h2>
          </div>
          <span v-if="selectedOrder" class="text-xs font-semibold px-2.5 py-1 rounded bg-blue-100 text-blue-800">
            Factura seleccionada: #{{ selectedOrder.invoice_number || selectedOrder.number_order }}
          </span>
        </div>

        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Buscar Factura / Venta</label>
              <div class="relative">
                <input
                  type="text"
                  v-model="orderSearch"
                  @input="onSearchOrders"
                  placeholder="Escriba N° Factura, N° Orden, o Nombre/Cédula del cliente..."
                  class="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <div v-if="isSearchingOrders" class="absolute right-3 top-2.5 text-gray-400">
                  <i class="fa fa-spinner fa-spin"></i>
                </div>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Seleccionar de la lista</label>
              <select
                v-model="form.order_id"
                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                required
              >
                <option value="">-- Seleccione una Factura / Orden --</option>
                <option v-for="order in orderList" :key="order.id" :value="order.id">
                  #{{ order.invoice_number || order.number_order }} - {{ order.client ? order.client.name : 'Sin cliente' }} (Total: ${{ formatNumber(order.total) }})
                </option>
              </select>
            </div>
          </div>

          <!-- RESUMEN DE LA ORDEN SELECCIONADA -->
          <div v-if="selectedOrder" class="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span class="text-gray-500 block text-xs uppercase font-medium">Cliente</span>
                <strong class="text-gray-800 text-base">{{ selectedOrder.client ? selectedOrder.client.name : 'N/A' }}</strong>
                <div class="text-xs text-gray-500">RIF/CI: {{ selectedOrder.client ? selectedOrder.client.identification : '-' }}</div>
              </div>
              <div>
                <span class="text-gray-500 block text-xs uppercase font-medium">Fecha de Venta</span>
                <span class="text-gray-800 font-medium">{{ formatDate(selectedOrder.created_at) }}</span>
                <div class="text-xs text-gray-500">Vendedor: {{ selectedOrder.responsible ? (selectedOrder.responsible.first_name + ' ' + (selectedOrder.responsible.last_name || '')) : 'N/A' }}</div>
              </div>
              <div>
                <span class="text-gray-500 block text-xs uppercase font-medium">Total Factura</span>
                <span class="text-lg font-bold text-gray-900">${{ formatNumber(selectedOrder.total) }}</span>
                <div class="text-xs text-gray-500">Pagado: ${{ formatNumber(selectedOrder.balance) }}</div>
              </div>
              <div>
                <span class="text-gray-500 block text-xs uppercase font-medium">Saldo Pendiente</span>
                <span class="text-lg font-bold" :class="orderPendingDebt > 0 ? 'text-red-600' : 'text-green-600'">
                  ${{ formatNumber(orderPendingDebt) }}
                </span>
                <div class="text-xs" :class="orderPendingDebt > 0 ? 'text-red-500' : 'text-green-500'">
                  {{ orderPendingDebt > 0 ? 'Pendiente de pago' : 'Totalmente pagada' }}
                </div>
              </div>
            </div>
          </div>

          <!-- TABLA DE PRODUCTOS DE LA FACTURA -->
          <div v-if="selectedOrder">
            <div class="flex items-center justify-between mb-3">
              <h3 class="font-semibold text-gray-800 text-sm uppercase tracking-wide">
                Seleccione los productos a devolver
              </h3>
              <span class="text-xs text-gray-500">Indique la cantidad a devolver y el motivo por cada ítem</span>
            </div>

            <div class="overflow-x-auto border border-gray-200 rounded-lg">
              <table class="w-full text-left text-sm">
                <thead class="bg-gray-100 text-gray-700 text-xs uppercase font-semibold">
                  <tr>
                    <th class="p-3 text-center w-12">Devolver</th>
                    <th class="p-3">Código</th>
                    <th class="p-3">Descripción</th>
                    <th class="p-3 text-center">Facturado</th>
                    <th class="p-3 text-center">Ya Devuelto</th>
                    <th class="p-3 text-center">Disponible</th>
                    <th class="p-3 text-right">Precio Unit.</th>
                    <th class="p-3 text-center w-28">Cant. a Devolver</th>
                    <th class="p-3 text-right">Subtotal</th>
                    <th class="p-3">Motivo</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                  <tr
                    v-for="product in availableOrderProducts"
                    :key="product.id"
                    :class="{'bg-yellow-50': form.products[product.id] && form.products[product.id].return_quantity > 0}"
                    class="hover:bg-gray-50 transition-colors"
                  >
                    <td class="p-3 text-center">
                      <input
                        type="checkbox"
                        :checked="form.products[product.id] && form.products[product.id].return_quantity > 0"
                        @change="toggleProductReturn(product, $event.target.checked)"
                        class="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                      />
                    </td>
                    <td class="p-3 font-mono text-xs">{{ product.product ? product.product.code : '-' }}</td>
                    <td class="p-3 font-medium text-gray-800">
                      {{ product.product ? product.product.name : 'Producto #' + product.product_id }}
                    </td>
                    <td class="p-3 text-center">{{ product.quantity }}</td>
                    <td class="p-3 text-center text-gray-500">{{ getAlreadyReturned(product) }}</td>
                    <td class="p-3 text-center font-bold text-blue-600">{{ getAvailableQuantity(product) }}</td>
                    <td class="p-3 text-right font-mono">${{ formatNumber(product.price) }}</td>
                    <td class="p-3">
                      <input
                        type="number"
                        min="0"
                        :max="getAvailableQuantity(product)"
                        v-model.number="form.products[product.id].return_quantity"
                        @input="updateProductAmount(product)"
                        class="w-full text-center px-2 py-1 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 text-sm font-semibold"
                      />
                    </td>
                    <td class="p-3 text-right font-mono font-bold text-gray-900">
                      ${{ formatNumber(form.products[product.id] ? form.products[product.id].return_amount : 0) }}
                    </td>
                    <td class="p-3">
                      <select
                        v-model="form.products[product.id].reason"
                        class="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Seleccione motivo</option>
                        <option value="Defectuoso / Garantía">Defectuoso / Garantía</option>
                        <option value="Error en pedido / Despacho">Error en pedido / Despacho</option>
                        <option value="Inconformidad del cliente">Inconformidad del cliente</option>
                        <option value="Cambio por otro producto">Cambio por otro producto</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </td>
                  </tr>

                  <tr v-if="!availableOrderProducts.length">
                    <td colspan="10" class="p-4 text-center text-gray-500">
                      Esta orden no tiene productos disponibles para devolución.
                    </td>
                  </tr>
                </tbody>
                <tfoot class="bg-gray-50 font-bold border-t-2 border-gray-200">
                  <tr>
                    <td colspan="8" class="p-3 text-right uppercase text-gray-700">Total Valor a Devolver:</td>
                    <td class="p-3 text-right font-mono text-base text-blue-700">
                      ${{ formatNumber(totalReturnedAmount) }}
                    </td>
                    <td class="p-3 text-xs text-gray-500">
                      <span v-if="change > 1">≈ Bs. {{ formatNumber(totalReturnedAmount * change) }}</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- PASO 2: DECISIÓN Y MODALIDAD DE RESOLUCIÓN -->
      <div v-if="selectedOrder && totalReturnedAmount > 0" class="bg-white rounded-lg shadow mb-6 border border-gray-100 overflow-hidden">
        <div class="px-6 py-4 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
          <div class="flex items-center space-x-2">
            <span class="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm">2</span>
            <h2 class="text-lg font-semibold text-gray-800">Decisión: ¿Cómo desea resolver esta devolución?</h2>
          </div>
        </div>

        <div class="p-6">
          <!-- TABS DE MODALIDADES -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <!-- OPCIÓN 1: DEVOLVER DINERO -->
            <label
              :class="{'border-blue-600 bg-blue-50 ring-2 ring-blue-500': form.resolution_type === 'refund', 'border-gray-200 hover:border-gray-300': form.resolution_type !== 'refund'}"
              class="border-2 rounded-xl p-4 cursor-pointer transition-all flex flex-col justify-between"
            >
              <div class="flex items-start justify-between">
                <div>
                  <div class="flex items-center space-x-2">
                    <i class="fa fa-money-bill-wave text-green-600 text-xl"></i>
                    <h3 class="font-bold text-gray-800">Devolver Dinero</h3>
                  </div>
                  <p class="text-xs text-gray-500 mt-1">Reembolsar el monto devuelto en efectivo, transferencia u otro método.</p>
                </div>
                <input type="radio" v-model="form.resolution_type" value="refund" class="text-blue-600 mt-1" />
              </div>
              <div class="mt-4 pt-2 border-t border-gray-200/60 text-xs font-semibold text-gray-700">
                Monto a reembolsar: <span class="text-green-700 font-bold">${{ formatNumber(totalReturnedAmount) }}</span>
              </div>
            </label>

            <!-- OPCIÓN 2: CAMBIAR PRODUCTO -->
            <label
              :class="{'border-blue-600 bg-blue-50 ring-2 ring-blue-500': form.resolution_type === 'exchange', 'border-gray-200 hover:border-gray-300': form.resolution_type !== 'exchange'}"
              class="border-2 rounded-xl p-4 cursor-pointer transition-all flex flex-col justify-between"
            >
              <div class="flex items-start justify-between">
                <div>
                  <div class="flex items-center space-x-2">
                    <i class="fa fa-boxes-stacked text-blue-600 text-xl"></i>
                    <h3 class="font-bold text-gray-800">Cambiar Producto</h3>
                  </div>
                  <p class="text-xs text-gray-500 mt-1">El cliente entrega este producto y se lleva otro(s) nuevo(s) de inventario.</p>
                </div>
                <input type="radio" v-model="form.resolution_type" value="exchange" class="text-blue-600 mt-1" />
              </div>
              <div class="mt-4 pt-2 border-t border-gray-200/60 text-xs font-semibold text-gray-700">
                Diferencia: <span :class="exchangeDifference >= 0 ? 'text-indigo-700' : 'text-green-700'" class="font-bold">${{ formatNumber(Math.abs(exchangeDifference)) }}</span>
              </div>
            </label>

            <!-- OPCIÓN 3: FONDO PARA VENTA MAYOR / ABONO -->
            <label
              :class="{'border-blue-600 bg-blue-50 ring-2 ring-blue-500': form.resolution_type === 'store_credit', 'border-gray-200 hover:border-gray-300': form.resolution_type !== 'store_credit'}"
              class="border-2 rounded-xl p-4 cursor-pointer transition-all flex flex-col justify-between"
            >
              <div class="flex items-start justify-between">
                <div>
                  <div class="flex items-center space-x-2">
                    <i class="fa fa-file-invoice-dollar text-purple-600 text-xl"></i>
                    <h3 class="font-bold text-gray-800">Fondo / Venta Mayor</h3>
                  </div>
                  <p class="text-xs text-gray-500 mt-1">Abonar directamente el monto a otra factura/orden con saldo pendiente del cliente.</p>
                </div>
                <input type="radio" v-model="form.resolution_type" value="store_credit" class="text-blue-600 mt-1" />
              </div>
              <div class="mt-4 pt-2 border-t border-gray-200/60 text-xs font-semibold text-gray-700">
                Fondo disponible: <span class="text-purple-700 font-bold">${{ formatNumber(totalReturnedAmount) }}</span>
              </div>
            </label>
          </div>

          <!-- DETALLES DE RESOLUCIÓN 1: REEMBOLSO DE DINERO -->
          <div v-if="form.resolution_type === 'refund'" class="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Método de Reembolso</label>
                <select v-model="form.refund_method" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" required>
                  <option value="">Seleccione método</option>
                  <option value="Efectivo">Efectivo ($ USD)</option>
                  <option value="Bolivares Efectivo">Bolívares en Efectivo</option>
                  <option value="Transferencia">Transferencia Bancaria</option>
                  <option value="Pago Móvil">Pago Móvil</option>
                  <option value="Zelle">Zelle</option>
                  <option v-for="method in payment_methods" :key="method.id" :value="method.name">
                    {{ method.name }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Referencia / Comprobante</label>
                <input
                  type="text"
                  v-model="form.refund_reference"
                  placeholder="N° de referencia o comprobante..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Caja Asociada</label>
                <div class="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700">
                  <span v-if="active_caja" class="text-green-600 font-medium">
                    <i class="fa fa-circle text-xs mr-1"></i> Caja Abierta #{{ active_caja.id }}
                  </span>
                  <span v-else class="text-gray-500">Sin caja abierta asignada</span>
                </div>
              </div>
            </div>

            <div class="text-xs text-gray-600 bg-white p-3 rounded border border-gray-200 flex items-center space-x-2">
              <i class="fa fa-info-circle text-blue-500 text-sm"></i>
              <span>
                Si la orden original fue pagada, el dinero saldrá del método/caja seleccionado. Si la orden fue a crédito con saldo pendiente, este monto reducirá directamente la deuda del cliente.
              </span>
            </div>
          </div>

          <!-- DETALLES DE RESOLUCIÓN 2: CAMBIO DE PRODUCTO -->
          <div v-if="form.resolution_type === 'exchange'" class="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Buscar y agregar producto a entregar en cambio</label>
              <div class="relative">
                <input
                  type="text"
                  v-model="productSearch"
                  @input="onSearchProducts"
                  placeholder="Escriba código o nombre del producto para buscar en inventario..."
                  class="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
                />
                <div v-if="isSearchingProducts" class="absolute right-3 top-2.5 text-gray-400">
                  <i class="fa fa-spinner fa-spin"></i>
                </div>
              </div>

              <!-- RESULTADOS DE BÚSQUEDA DE PRODUCTOS -->
              <div v-if="productSearchResults.length" class="mt-2 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto z-10">
                <div
                  v-for="prod in productSearchResults"
                  :key="prod.id"
                  @click="addExchangeProduct(prod)"
                  class="p-2.5 hover:bg-blue-50 cursor-pointer border-b border-gray-100 flex justify-between items-center text-sm"
                >
                  <div>
                    <span class="font-semibold text-gray-800">{{ prod.name }}</span>
                    <span class="text-xs font-mono text-gray-500 ml-2">({{ prod.code }})</span>
                  </div>
                  <div class="text-right">
                    <span class="text-xs text-gray-500 mr-3">Stock: <strong>{{ prod.stock }}</strong></span>
                    <span class="font-bold font-mono text-blue-600">${{ formatNumber(prod.price) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- TABLA DE PRODUCTOS ENTREGADOS A CAMBIO -->
            <div>
              <h4 class="text-xs font-bold uppercase text-gray-700 mb-2">Productos que se entregan al cliente a cambio:</h4>
              <div class="overflow-x-auto border border-gray-200 rounded-lg bg-white">
                <table class="w-full text-left text-sm">
                  <thead class="bg-gray-100 text-gray-700 text-xs uppercase">
                    <tr>
                      <th class="p-2.5">Código</th>
                      <th class="p-2.5">Producto</th>
                      <th class="p-2.5 text-center">Stock</th>
                      <th class="p-2.5 text-center w-28">Cant. Entregar</th>
                      <th class="p-2.5 text-right w-28">Precio Unit.</th>
                      <th class="p-2.5 text-right">Total</th>
                      <th class="p-2.5 text-center w-12"></th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100">
                    <tr v-for="(item, index) in exchangeProducts" :key="index">
                      <td class="p-2.5 font-mono text-xs">{{ item.code }}</td>
                      <td class="p-2.5 font-medium">{{ item.name }}</td>
                      <td class="p-2.5 text-center text-xs text-gray-500">{{ item.stock }}</td>
                      <td class="p-2.5">
                        <input
                          type="number"
                          min="1"
                          :max="item.stock || 9999"
                          v-model.number="item.quantity"
                          class="w-full text-center px-2 py-1 border border-gray-300 rounded text-sm font-semibold"
                        />
                      </td>
                      <td class="p-2.5">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          v-model.number="item.price"
                          class="w-full text-right px-2 py-1 border border-gray-300 rounded text-sm font-semibold"
                        />
                      </td>
                      <td class="p-2.5 text-right font-mono font-bold text-gray-800">
                        ${{ formatNumber(item.quantity * item.price) }}
                      </td>
                      <td class="p-2.5 text-center">
                        <button type="button" @click="removeExchangeProduct(index)" class="text-red-500 hover:text-red-700">
                          <i class="fa fa-trash"></i>
                        </button>
                      </td>
                    </tr>

                    <tr v-if="!exchangeProducts.length">
                      <td colspan="7" class="p-4 text-center text-gray-400 text-xs italic">
                        Use el buscador arriba para agregar los nuevos productos que se entregarán en cambio.
                      </td>
                    </tr>
                  </tbody>
                  <tfoot class="bg-gray-50 font-bold border-t">
                    <tr>
                      <td colspan="5" class="p-2.5 text-right uppercase text-xs">Total Productos Entregados:</td>
                      <td class="p-2.5 text-right font-mono text-indigo-700">
                        ${{ formatNumber(totalExchangeAmount) }}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <!-- COMPARATIVA Y BALANCE DE DIFERENCIA -->
            <div class="bg-white p-4 rounded-lg border border-gray-200">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm items-center">
                <div>
                  <span class="text-xs text-gray-500 uppercase block">Total Productos Devueltos</span>
                  <span class="text-base font-bold text-gray-800 font-mono">${{ formatNumber(totalReturnedAmount) }}</span>
                </div>
                <div>
                  <span class="text-xs text-gray-500 uppercase block">Total Productos Entregados</span>
                  <span class="text-base font-bold text-indigo-700 font-mono">${{ formatNumber(totalExchangeAmount) }}</span>
                </div>
                <div class="p-3 rounded-lg" :class="exchangeDifference > 0 ? 'bg-red-50 border border-red-200' : (exchangeDifference < 0 ? 'bg-green-50 border border-green-200' : 'bg-gray-50')">
                  <span class="text-xs uppercase font-bold block" :class="exchangeDifference > 0 ? 'text-red-700' : (exchangeDifference < 0 ? 'text-green-700' : 'text-gray-700')">
                    {{ exchangeDifference > 0 ? 'Diferencia a Pagar por Cliente' : (exchangeDifference < 0 ? 'Diferencia a Favor del Cliente' : 'Intercambio Equivalente') }}
                  </span>
                  <span class="text-lg font-bold font-mono" :class="exchangeDifference > 0 ? 'text-red-700' : (exchangeDifference < 0 ? 'text-green-700' : 'text-gray-700')">
                    ${{ formatNumber(Math.abs(exchangeDifference)) }}
                  </span>
                </div>
              </div>

              <!-- SI EL CLIENTE DEBE PAGAR LA DIFERENCIA -->
              <div v-if="exchangeDifference > 0" class="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs font-bold text-gray-700 mb-1 uppercase">¿Cómo paga el cliente la diferencia?</label>
                  <select v-model="form.difference_payment_method" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                    <option value="">Cargar a la cuenta del cliente (deuda)</option>
                    <option value="Efectivo">Efectivo ($ USD)</option>
                    <option value="Bolivares Efectivo">Bolívares en Efectivo</option>
                    <option value="Transferencia">Transferencia</option>
                    <option value="Pago Móvil">Pago Móvil</option>
                    <option value="Zelle">Zelle</option>
                  </select>
                </div>
              </div>

              <!-- SI QUEDA DIFERENCIA A FAVOR DEL CLIENTE -->
              <div v-if="exchangeDifference < 0" class="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs font-bold text-gray-700 mb-1 uppercase">¿Cómo devolver el sobrante al cliente?</label>
                  <select v-model="form.refund_method" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                    <option value="">Dejar como saldo a favor en cuenta</option>
                    <option value="Efectivo">Reembolso en Efectivo ($ USD)</option>
                    <option value="Bolivares Efectivo">Reembolso en Bolívares Efectivo</option>
                    <option value="Transferencia">Transferencia Bancaria</option>
                    <option value="Pago Móvil">Pago Móvil</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- DETALLES DE RESOLUCIÓN 3: FONDO PARA VENTA MAYOR / ABONO A FACTURA -->
          <div v-if="form.resolution_type === 'store_credit'" class="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-4">
            <div>
              <h4 class="text-sm font-bold text-purple-900 mb-1">
                Seleccione la Factura / Venta a la que desea aplicar este fondo como abono:
              </h4>
              <p class="text-xs text-purple-700 mb-3">
                El monto de ${{ formatNumber(totalReturnedAmount) }} se registrará como un abono directo a la orden destino seleccionada.
              </p>

              <!-- LISTA DE OTRAS FACTURAS PENDIENTES DEL MISMO CLIENTE -->
              <div v-if="isLoadingClientOrders" class="py-4 text-center text-sm text-purple-700">
                <i class="fa fa-spinner fa-spin mr-2"></i> Buscando facturas pendientes del cliente...
              </div>

              <div v-else class="overflow-x-auto border border-purple-200 rounded-lg bg-white">
                <table class="w-full text-left text-sm">
                  <thead class="bg-purple-100 text-purple-900 text-xs uppercase font-semibold">
                    <tr>
                      <th class="p-3 text-center w-12">Aplicar</th>
                      <th class="p-3">N° Factura / Orden</th>
                      <th class="p-3">Fecha</th>
                      <th class="p-3 text-right">Total Factura</th>
                      <th class="p-3 text-right">Saldo Actual</th>
                      <th class="p-3 text-right">Saldo Tras Abono</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-purple-100">
                    <tr
                      v-for="target in clientPendingOrders"
                      :key="target.id"
                      :class="{'bg-purple-50': form.target_order_id === target.id}"
                      class="hover:bg-purple-50 cursor-pointer transition-colors"
                      @click="form.target_order_id = target.id"
                    >
                      <td class="p-3 text-center">
                        <input
                          type="radio"
                          v-model="form.target_order_id"
                          :value="target.id"
                          class="text-purple-600 focus:ring-purple-500"
                        />
                      </td>
                      <td class="p-3 font-bold text-purple-900">
                        #{{ target.invoice_number || target.number_order }}
                      </td>
                      <td class="p-3 text-gray-600 text-xs">{{ formatDate(target.created_at) }}</td>
                      <td class="p-3 text-right font-mono">${{ formatNumber(target.total) }}</td>
                      <td class="p-3 text-right font-mono font-bold text-red-600">
                        ${{ formatNumber(Math.max(0, target.total - target.balance)) }}
                      </td>
                      <td class="p-3 text-right font-mono font-bold text-green-700">
                        ${{ formatNumber(Math.max(0, (target.total - target.balance) - totalReturnedAmount)) }}
                      </td>
                    </tr>

                    <tr v-if="!clientPendingOrders.length">
                      <td colspan="6" class="p-4 text-center text-gray-500 text-xs">
                        Este cliente no tiene otras facturas con saldo pendiente. Si continúa, el monto quedará acreditado a su saldo global de cliente.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- PASO 3: NOTAS Y ACCIONES -->
      <div v-if="selectedOrder && totalReturnedAmount > 0" class="bg-white rounded-lg shadow mb-6 border border-gray-100 p-6">
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Notas / Observaciones Generales</label>
          <textarea
            v-model="form.notes"
            rows="3"
            placeholder="Escriba aquí detalles relevantes sobre la devolución, estado físico del producto, etc..."
            class="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
          ></textarea>
        </div>

        <div class="flex items-center justify-between pt-4 border-t border-gray-100">
          <label class="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              v-model="form.approve_now"
              class="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
            />
            <span class="font-medium">Aprobar y procesar inmediatamente (actualiza inventario y finanzas)</span>
          </label>

          <div class="flex items-center space-x-3">
            <Link href="/devolutions" class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium text-sm transition-colors">
              Cancelar
            </Link>
            <button
              type="submit"
              :disabled="form.processing || totalReturnedAmount <= 0"
              class="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-bold text-sm shadow transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              <i v-if="form.processing" class="fa fa-spinner fa-spin"></i>
              <span>Procesar Devolución</span>
            </button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { Head, Link, useForm } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import axios from 'axios'
import debounce from 'lodash/debounce'

export default {
  components: {
    Head,
    Link,
  },
  layout: Layout,
  props: {
    orders: {
      type: Array,
      default: () => []
    },
    preselected_order_id: {
      type: Number,
      default: null
    },
    payment_methods: {
      type: Array,
      default: () => []
    },
    active_caja: {
      type: Object,
      default: null
    },
    change: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      orderSearch: '',
      isSearchingOrders: false,
      orderList: [...this.orders],
      productSearch: '',
      isSearchingProducts: false,
      productSearchResults: [],
      exchangeProducts: [],
      clientPendingOrders: [],
      isLoadingClientOrders: false,
    }
  },
  setup(props) {
    const form = useForm({
      order_id: props.preselected_order_id || '',
      return_type: 'partial',
      resolution_type: 'refund',
      target_order_id: null,
      products: {},
      exchange_products: [],
      refund_method: 'Efectivo',
      refund_reference: '',
      difference_payment_method: 'Efectivo',
      caja_id: props.active_caja ? props.active_caja.id : null,
      notes: '',
      approve_now: true,
    })

    return { form }
  },
  computed: {
    selectedOrder() {
      return this.orderList.find(o => o.id === this.form.order_id) || null
    },
    orderPendingDebt() {
      if (!this.selectedOrder) return 0
      return Math.max(0, this.selectedOrder.total - this.selectedOrder.balance)
    },
    availableOrderProducts() {
      if (!this.selectedOrder || !this.selectedOrder.products) return []
      return this.selectedOrder.products.filter(p => this.getAvailableQuantity(p) > 0)
    },
    totalReturnedAmount() {
      if (!this.form.products) return 0
      return Object.values(this.form.products).reduce((sum, item) => {
        return sum + (Number(item.return_amount) || 0)
      }, 0)
    },
    totalExchangeAmount() {
      return this.exchangeProducts.reduce((sum, item) => {
        return sum + ((Number(item.quantity) || 0) * (Number(item.price) || 0))
      }, 0)
    },
    exchangeDifference() {
      // exchange_amount - total_amount
      return this.totalExchangeAmount - this.totalReturnedAmount
    }
  },
  watch: {
    'form.order_id': {
      handler(newOrderId) {
        if (newOrderId) {
          this.initOrderProducts(newOrderId)
          this.fetchClientPendingOrders(newOrderId)
        }
      },
      immediate: true
    }
  },
  methods: {
    formatNumber(val) {
      const num = Number(val) || 0
      return num.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString('es-VE')
    },
    getAlreadyReturned(product) {
      if (!product.devolution_products || !product.devolution_products.length) return 0
      return product.devolution_products
        .filter(dp => dp.devolution && dp.devolution.status !== 'rejected')
        .reduce((sum, dp) => sum + Number(dp.quantity), 0)
    },
    getAvailableQuantity(product) {
      const returned = this.getAlreadyReturned(product)
      return Math.max(0, Number(product.quantity) - returned)
    },
    initOrderProducts(orderId) {
      const order = this.orderList.find(o => o.id === orderId)
      if (!order || !order.products) return

      const productsObj = {}
      order.products.forEach(p => {
        productsObj[p.id] = {
          return_quantity: 0,
          return_amount: 0,
          price: p.price,
          reason: ''
        }
      })
      this.form.products = productsObj
      this.exchangeProducts = []
    },
    toggleProductReturn(product, isChecked) {
      if (!this.form.products[product.id]) return
      if (isChecked) {
        const available = this.getAvailableQuantity(product)
        this.form.products[product.id].return_quantity = available
        this.form.products[product.id].return_amount = available * Number(product.price)
      } else {
        this.form.products[product.id].return_quantity = 0
        this.form.products[product.id].return_amount = 0
      }
    },
    updateProductAmount(product) {
      const item = this.form.products[product.id]
      if (!item) return
      const maxQty = this.getAvailableQuantity(product)
      if (item.return_quantity > maxQty) {
        item.return_quantity = maxQty
      }
      if (item.return_quantity < 0) {
        item.return_quantity = 0
      }
      item.return_amount = (Number(item.return_quantity) || 0) * Number(product.price)
    },
    onSearchOrders: debounce(function () {
      if (!this.orderSearch || this.orderSearch.length < 2) return
      this.isSearchingOrders = true
      axios.get(`/api/devolutions/search-orders?search=${encodeURIComponent(this.orderSearch)}`)
        .then(res => {
          if (res.data && res.data.length) {
            // Unir sin duplicados
            const existingIds = new Set(this.orderList.map(o => o.id))
            res.data.forEach(o => {
              if (!existingIds.has(o.id)) {
                this.orderList.push(o)
              }
            })
          }
        })
        .finally(() => {
          this.isSearchingOrders = false
        })
    }, 400),
    fetchClientPendingOrders(orderId) {
      const order = this.orderList.find(o => o.id === orderId)
      if (!order || !order.client_id) {
        this.clientPendingOrders = []
        return
      }

      this.isLoadingClientOrders = true
      axios.get(`/api/devolutions/search-orders?client_id=${order.client_id}`)
        .then(res => {
          if (res.data) {
            // Filtrar las que tienen deuda pendiente y no son la misma orden actual
            this.clientPendingOrders = res.data.filter(o => o.id !== order.id && (o.total - o.balance) > 0.01)
          }
        })
        .finally(() => {
          this.isLoadingClientOrders = false
        })
    },
    onSearchProducts: debounce(function () {
      if (!this.productSearch || this.productSearch.length < 2) {
        this.productSearchResults = []
        return
      }
      this.isSearchingProducts = true
      axios.get(`/api/devolutions/search-products?search=${encodeURIComponent(this.productSearch)}`)
        .then(res => {
          this.productSearchResults = res.data || []
        })
        .finally(() => {
          this.isSearchingProducts = false
        })
    }, 400),
    addExchangeProduct(product) {
      // Verificar si ya está en la lista
      const exists = this.exchangeProducts.find(p => p.product_id === product.id)
      if (exists) {
        exists.quantity += 1
      } else {
        this.exchangeProducts.push({
          product_id: product.id,
          name: product.name,
          code: product.code,
          stock: product.stock,
          price: product.price,
          quantity: 1,
          deposit_id: product.deposit_id
        })
      }
      this.productSearch = ''
      this.productSearchResults = []
    },
    removeExchangeProduct(index) {
      this.exchangeProducts.splice(index, 1)
    },
    submit() {
      if (this.totalReturnedAmount <= 0) {
        alert('Debe seleccionar al menos un producto y especificar la cantidad a devolver.')
        return
      }

      if (this.form.resolution_type === 'exchange' && !this.exchangeProducts.length) {
        alert('Para la modalidad de cambio debe agregar al menos un producto a entregar al cliente.')
        return
      }

      // Sincronizar productos de intercambio al formulario
      this.form.exchange_products = this.exchangeProducts.map(p => ({
        product_id: p.product_id,
        deposit_id: p.deposit_id,
        quantity: p.quantity,
        price: p.price
      }))

      this.form.post('/devolutions')
    }
  }
}
</script>
